import React, { useState, useEffect } from 'react';
import { admin } from '../../services/api';
import DateRangePicker from './DateRangePicker';
import PayrollExport from './PayrollExport';
import '../../styles/AdminAnalytics.css';

function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    comparePrevious: false
  });
  const [expandedReviewers, setExpandedReviewers] = useState(new Set());

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async (params = {}) => {
    setLoading(true);
    try {
      const response = await admin.getAnalytics(params);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      if (err.response?.status === 404) {
        // Show placeholder data if endpoint doesn't exist
        setAnalytics({
          stuck_medications: [],
          status_overview: {
            available: 0,
            claimed: 0,
            in_review: 0,
            approved: 0,
            published: 0,
          },
          reviewer_performance: [],
          recent_activity: [],
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = ({ startDate, endDate, comparePrevious }) => {
    setDateRange({ startDate, endDate, comparePrevious });
    
    const params = {};
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
      params.compare_previous = comparePrevious;
    }
    
    fetchAnalytics(params);
  };

  const handleForceReleaseAll = async () => {
    const currentData = analytics.current || analytics;
    if (window.confirm(`Release ALL ${currentData.stuck_medications.length} stuck medications?`)) {
      try {
        await admin.bulkReleaseStuck();
        alert('All stuck medications released successfully!');
        fetchAnalytics(dateRange.startDate && dateRange.endDate ? {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          compare_previous: dateRange.comparePrevious
        } : {});
      } catch (err) {
        console.error('Bulk release failed:', err);
        alert('Bulk release failed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleForceSingleRelease = async (id, name) => {
    if (window.confirm(`Release "${name}"?`)) {
      try {
        await admin.forceRelease(id);
        alert('Medication released successfully!');
        fetchAnalytics(dateRange.startDate && dateRange.endDate ? {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          compare_previous: dateRange.comparePrevious
        } : {});
      } catch (err) {
        console.error('Release failed:', err);
        alert('Release failed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const toggleReviewerExpanded = (userId) => {
    const newExpanded = new Set(expandedReviewers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedReviewers(newExpanded);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = Date.now();
    const diff = now - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h ago`;
    }
  };

  const formatAvgTime = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const renderSpeedTrends = (trends) => {
    if (!trends || trends.length === 0) return <p className="no-data">Not enough data</p>;

    const maxTime = Math.max(...trends.map(t => t.avg_minutes));
    
    return (
      <div className="speed-trends-chart">
        {trends.map((trend, idx) => (
          <div key={idx} className="trend-bar-container">
            <div className="trend-month">{trend.month}</div>
            <div className="trend-bar-wrapper">
              <div 
                className="trend-bar"
                style={{ 
                  width: `${(trend.avg_minutes / maxTime) * 100}%`,
                  backgroundColor: idx === trends.length - 1 ? '#4CAF50' : '#2196F3'
                }}
              />
              <span className="trend-value">{Math.round(trend.avg_minutes)}m</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderConsistencyScore = (score) => {
    const percentage = Math.round(score * 100);
    const color = score >= 0.8 ? '#4CAF50' : score >= 0.6 ? '#FF9800' : '#F44336';
    
    return (
      <div className="consistency-indicator">
        <div className="consistency-bar-bg">
          <div 
            className="consistency-bar-fill"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
        <span className="consistency-value">{percentage}%</span>
      </div>
    );
  };

  const renderComplexityTable = (complexity) => {
    if (!complexity || complexity.length === 0) return <p className="no-data">No drug class data</p>;

    return (
      <table className="complexity-table">
        <thead>
          <tr>
            <th>Drug Class</th>
            <th>Avg Time</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {complexity.slice(0, 5).map((item, idx) => (
            <tr key={idx}>
              <td>{item.drug_class}</td>
              <td>{formatAvgTime(item.avg_minutes)}</td>
              <td>{item.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  // Handle comparison mode
  const hasComparison = analytics.current && analytics.previous;
  const currentData = hasComparison ? analytics.current : analytics;
  const previousData = hasComparison ? analytics.previous : null;

  const { stuck_medications, status_overview, reviewer_performance, recent_activity } = currentData;

  return (
    <div className="admin-analytics">
      {/* Date Range Filter */}
      <div className="analytics-header">
        <h2>📊 Analytics Dashboard</h2>
        <DateRangePicker onDateChange={handleDateChange} />
      </div>

      {/* Stuck Medications Alert */}
      {stuck_medications && stuck_medications.length > 0 && (
        <div className="alert-section stuck-alert">
          <div className="alert-header">
            <h3>⚠️ Stuck Medications Alert</h3>
            <button type="button" className="btn-warning" onClick={handleForceReleaseAll}>
              Force Release All ({stuck_medications.length})
            </button>
          </div>
          <p className="alert-description">
            These medications have been claimed for more than 24 hours without progress.
          </p>
          <div className="stuck-list">
            {stuck_medications.map(med => (
              <div key={med.id} className="stuck-item">
                <div className="stuck-info">
                  <strong>{med.name}</strong>
                  <span className="stuck-meta">
                    Reviewer: {med.reviewer_name || 'Unknown'} | 
                    Claimed: {formatDateTime(med.claimed_at)}
                  </span>
                </div>
                <button 
                  type="button"
                  className="btn-small btn-warning"
                  onClick={() => handleForceSingleRelease(med.id, med.name)}
                >
                  Release
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Medication Status Overview */}
      <div className="analytics-section">
        <h3>📊 Medication Status Overview</h3>
        <div className="status-grid">
          <div className="status-card card-available">
            <div className="status-count">{status_overview?.available || 0}</div>
            <div className="status-label">Available</div>
            {previousData && (
              <div className="status-comparison">
                {getComparisonText(previousData.status_overview.available, status_overview.available)}
              </div>
            )}
          </div>
          <div className="status-card card-claimed">
            <div className="status-count">{status_overview?.claimed || 0}</div>
            <div className="status-label">Claimed</div>
            {previousData && (
              <div className="status-comparison">
                {getComparisonText(previousData.status_overview.claimed, status_overview.claimed)}
              </div>
            )}
          </div>
          <div className="status-card card-review">
            <div className="status-count">{status_overview?.in_review || 0}</div>
            <div className="status-label">In Review</div>
            {previousData && (
              <div className="status-comparison">
                {getComparisonText(previousData.status_overview.in_review, status_overview.in_review)}
              </div>
            )}
          </div>
          <div className="status-card card-approved">
            <div className="status-count">{status_overview?.approved || 0}</div>
            <div className="status-label">Approved</div>
            {previousData && (
              <div className="status-comparison">
                {getComparisonText(previousData.status_overview.approved, status_overview.approved)}
              </div>
            )}
          </div>
          <div className="status-card card-published">
            <div className="status-count">{status_overview?.published || 0}</div>
            <div className="status-label">Published</div>
            {previousData && (
              <div className="status-comparison">
                {getComparisonText(previousData.status_overview.published, status_overview.published)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Reviewer Performance */}
      <div className="analytics-section">
        <h3>👥 Enhanced Reviewer Performance</h3>
        {reviewer_performance && reviewer_performance.length > 0 ? (
          <div className="reviewer-cards">
            {reviewer_performance.map((reviewer) => (
              <div 
                key={reviewer.user_id} 
                className={`reviewer-card ${expandedReviewers.has(reviewer.user_id) ? 'expanded' : ''}`}
              >
                <div 
                  className="reviewer-header"
                  onClick={() => toggleReviewerExpanded(reviewer.user_id)}
                >
                  <div className="reviewer-info">
                    <strong>{reviewer.full_name}</strong>
                    {reviewer.credentials && <span className="credentials"> ({reviewer.credentials})</span>}
                  </div>
                  <div className="reviewer-summary">
                    <span className="summary-item">{reviewer.total_reviewed} reviewed</span>
                    <span className="summary-item">{formatAvgTime(reviewer.avg_time_minutes)} avg</span>
                    <span className="expand-icon">{expandedReviewers.has(reviewer.user_id) ? '▼' : '▶'}</span>
                  </div>
                </div>

                {expandedReviewers.has(reviewer.user_id) && (
                  <div className="reviewer-details">
                    <div className="detail-section">
                      <h4>📈 Speed Trends (Last 6 Months)</h4>
                      {renderSpeedTrends(reviewer.speed_trends)}
                    </div>

                    <div className="detail-section">
                      <h4>🎯 Consistency Score</h4>
                      {renderConsistencyScore(reviewer.consistency_score)}
                      <p className="consistency-note">
                        {reviewer.consistency_score >= 0.8 
                          ? 'Highly consistent review times' 
                          : reviewer.consistency_score >= 0.6
                          ? 'Moderately consistent review times'
                          : 'Variable review times - may need attention'}
                      </p>
                    </div>

                    <div className="detail-section">
                      <h4>💊 Complexity by Drug Class</h4>
                      {renderComplexityTable(reviewer.complexity_by_class)}
                    </div>

                    <div className="detail-section">
                      <p className="last-active">
                        <strong>Last Active:</strong> {formatDateTime(reviewer.last_active)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No reviewer activity yet.</p>
        )}
      </div>

      {/* Recent Activity */}
      <div className="analytics-section">
        <h3>📝 Recent Activity</h3>
        {recent_activity && recent_activity.length > 0 ? (
          <div className="activity-list">
            {recent_activity.map((activity, idx) => (
              <div key={idx} className="activity-item">
                <div className="activity-icon">✅</div>
                <div className="activity-content">
                  <div className="activity-main">
                    <strong>{activity.medication_name}</strong> approved by{' '}
                    <strong>{activity.reviewer_name}</strong>
                  </div>
                  <div className="activity-time">
                    {formatDateTime(activity.approved_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No recent approvals.</p>
        )}
      </div>

      {/* Payroll Export Section */}
      <PayrollExport />
    </div>
  );
}

// Helper function for comparison text
function getComparisonText(previous, current) {
  const diff = current - previous;
  if (diff > 0) {
    return <span className="comparison-up">+{diff}</span>;
  } else if (diff < 0) {
    return <span className="comparison-down">{diff}</span>;
  } else {
    return <span className="comparison-same">—</span>;
  }
}

export default Analytics;
